/**
 * CustomTypeModal.tsx — 8212-A
 *
 * Modal dialog for creating and editing custom feedback types.
 * Used from:
 *   - FeedbackTypeSelector "+ Add Custom Type" footer (create mode)
 *   - CustomPluginRow edit icon (edit mode, pre-filled)
 *
 * Design ref: docs/feedback-project/designs/renderings/feedback-types/Custom Edit Dialog.png
 * API patterns taken from SuggestionTypes.js (lines 254-303 create, 212-252 update).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import styled from "styled-components";
import {
  MAX_FEEDBACK_INSTRUCTION_WORDS,
  countFeedbackInstructionWords,
} from "../../../../../src/shared/feedbackInstructionsLimits";

// ---------------------------------------------------------------------------
// Example presets (reused from SuggestionTypes.js)
// ---------------------------------------------------------------------------

export const EXAMPLE_CUSTOM_INSTRUCTIONS = [
  {
    name: "Show Don't Tell",
    content: `Telling summarizes feelings, states, or judgments for the reader ("was happy," "felt cold," "it was beautiful"). Showing lets readers infer those things through concrete actions, dialogue, and vivid sensory details—what the characters do, say, and perceive. Make sure the suggestions are relevant to the Story Bible.

Examples:

Original: Maria was scared.
Suggestion: Maria's hands shook as she backed against the closet door, her breath rasping in short bursts.

Original: The city was bustling with energy.
Suggestion: Bicycles darted between honking taxis while street vendors sang out lunch specials above the pulse of dance music leaking from cafés.

Original: He was angry with her.
Suggestion: He kicked the chair aside and spoke through clenched teeth, "You knew this would happen, and you didn't tell me."`,
  },
  {
    name: "Avoid Phrases",
    content: `Avoid these phrases. Flag every occurrence you find and suggest a way to rewrite it.

Phrases to avoid:

at the end of the day
needless to say
for what it's worth
in my opinion / in my humble opinion
to be honest / to be frank
it goes without saying
last but not least
without further ado
in this day and age`,
  },
  {
    name: "Transition Checker",
    content: `Sudden leaps between settings, timeframes, or points of view can disorient the reader. Flag spots where the narrative jumps without a clear bridge, then propose a brief, scene-appropriate connective line (or two) that grounds the reader in where and when the story has moved. Keep fixes concise, using sensory cues, establishing details, or a beat of character action to ease the shift.

Examples
Original:
Tom slammed the kitchen door.
Three hours later, the search party trudged through the forest.

Suggested fix:
Tom slammed the kitchen door. By nightfall, after the town's alarms had rung and volunteers had gathered, the search party trudged through the forest.

Original:
She collapsed onto her dorm bed.
The next paragraph opened with her at graduation, cap in hand.

Suggested fix:
She collapsed onto her dorm bed. Four whirlwind years, dozens of sleepless nights, and one thesis later, she stood at graduation, cap in hand.

Original:
The detective thumbed through reports in his cluttered office.
The story cut to a windswept cliff where the killer waited.

Suggested fix:
The detective thumbed through reports in his cluttered office. Miles away, beyond the city's last streetlamp, a lone figure waited on a windswept cliff.`,
  },
  {
    name: "Deepen POV",
    content: `Point out where the narration stays on the surface—reporting actions or emotions from the outside—can distance the reader. Flag sentences that merely label a feeling or observation, then supply a brief rewrite that dives inside the character: use visceral sensations, thought-fragments, or metaphors filtered through that character's worldview. Keep each fix tight, vivid, and true to the Story Bible.

Examples
Original:
Sarah stepped into the alley and felt afraid.

Suggested fix:
Brick walls tilted toward her like closing jaws. She forced one foot past the other, praying the echo behind her belonged to no one.

Original:
Liam realized the boss was lying to him.

Suggested fix:
The minute Mr. Crane's voice softened, something snagged in Liam's gut—too smooth, too practiced. He's hiding the numbers, Liam thought, heat prickling up his neck.

Original:
The storm made Maya angry.

Suggested fix:
Rain slammed the windshield until the wipers squealed. Maya clenched the wheel, knuckles blanching—of course the sky had to unravel now, the one hour she needed it calm.`,
  },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CustomTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, instructions: string) => void | Promise<void>;
  /** Pre-fill for edit mode */
  initialName?: string;
  /** Pre-fill for edit mode */
  initialInstructions?: string;
  /** Plugin ID when editing (null/undefined for create mode) */
  pluginId?: string | null;
}

// ---------------------------------------------------------------------------
// Modal styles (react-modal inline style objects)
// ---------------------------------------------------------------------------

const modalStyles = {
  content: {
    position: "relative" as const,
    top: "auto",
    left: "auto",
    right: "auto",
    bottom: "auto",
    border: "none",
    borderRadius: "16px",
    padding: "0",
    width: "min(90vw, 800px)",
    maxHeight: "90vh",
    overflow: "auto" as const,
    boxShadow:
      "0px 1.66px 1.66px rgba(0, 0, 0, 0.15), 0px 0px 12.46px rgba(0, 0, 0, 0.15)",
    background: "var(--menu-color, #fff)",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 40000,
  },
};

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 12px;
  text-align: left;
`;

const ModalTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: var(--grey-0);
  line-height: 1.4;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 16px;
  font-weight: 500;
  color: var(--grey-0);
  line-height: 150%;
`;

const NameInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 10px;
  font-size: 16px;
  font-family: inherit;
  color: var(--grey-0);
  background: var(--menu-color, #fff);
  border: 1px solid var(--grey-3, #e5e5e5);
  border-radius: 5px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  outline: none;

  &:focus {
    border-color: var(--primary-cta, #4808d1);
  }

  &::placeholder {
    color: var(--grey-0, rgba(0, 0, 0, 0.5));
    opacity: 0.5;
  }
`;

const InstructionsTextarea = styled.textarea<{ $overLimit?: boolean }>`
  width: 100%;
  min-height: 200px;
  padding: 8px 10px;
  font-size: 16px;
  font-family: inherit;
  color: var(--grey-0);
  background: var(--menu-color, #fff);
  border: 1px solid ${(props) => (props.$overLimit ? "var(--danger-color)" : "var(--grey-3, #e5e5e5)")};
  border-radius: 5px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  outline: none;
  resize: vertical;
  line-height: 150%;

  &:focus {
    border-color: ${(props) => (props.$overLimit ? "var(--danger-color)" : "var(--primary-cta, #4808d1)")};
  }

  &::placeholder {
    color: var(--grey-0, rgba(0, 0, 0, 0.5));
    opacity: 0.5;
  }
`;

const WordCount = styled.div<{ $overLimit: boolean }>`
  font-size: 13px;
  line-height: 130%;
  color: ${(props) => (props.$overLimit ? "var(--danger-color)" : "var(--grey-0)")};
  opacity: ${(props) => (props.$overLimit ? 1 : 0.7)};
  align-self: flex-end;
`;

const ExamplesRow = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 130%;
  color: var(--grey-0);
  opacity: 0.7;
`;

const ExampleLink = styled.span`
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-top: 16px;
  gap: 8px;
`;

const CancelButton = styled.div`
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 16px;
  font-size: 16px;
  font-weight: 500;
  color: var(--primary-cta, #4808d1);
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const SaveButton = styled.div`
  height: 40px;
  border-radius: 8px;
  background-color: var(--primary-cta, #4808d1);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 16px;
  font-size: 16px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.15s ease;

  &.disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:not(.disabled):hover {
    opacity: 0.9;
  }
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CustomTypeModal({
  isOpen,
  onClose,
  onSave,
  initialName = "",
  initialInstructions = "",
  pluginId,
}: CustomTypeModalProps): JSX.Element {
  const [name, setName] = useState(initialName);
  const [instructions, setInstructions] = useState(initialInstructions);
  const [isSaving, setIsSaving] = useState(false);

  // Reset state when the modal opens with new initial values
  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setInstructions(initialInstructions);
      setIsSaving(false);
    }
  }, [isOpen, initialName, initialInstructions]);

  const wordCount = useMemo(() => countFeedbackInstructionWords(instructions), [instructions]);
  const isOverWordLimit = wordCount > MAX_FEEDBACK_INSTRUCTION_WORDS;

  const canSave =
    name.trim().length > 0 &&
    instructions.trim().length > 0 &&
    !isOverWordLimit &&
    !isSaving;

  const handleExampleClick = useCallback(
    (example: { name: string; content: string }) => {
      setName(example.name);
      setInstructions(example.content);
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      await onSave(name.trim(), instructions.trim());
    } finally {
      setIsSaving(false);
    }
  }, [canSave, name, instructions, onSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && e.metaKey && canSave) {
        e.preventDefault();
        handleSave();
      }
    },
    [canSave, handleSave]
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyles}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={true}
    >
      <ModalContainer
        className="custom-type-modal"
        onKeyDown={handleKeyDown}
      >
        <ModalTitle>Custom Feedback</ModalTitle>

        <FieldGroup>
          <FieldLabel htmlFor="custom-type-name">Name</FieldLabel>
          <NameInput
            id="custom-type-name"
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSaving}
            maxLength={100}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="custom-type-instructions">
            Custom Instructions
          </FieldLabel>
          <InstructionsTextarea
            id="custom-type-instructions"
            placeholder="Describe the patterns or issues you want checked in your document. Be specific. You can include example issues and how you would fix them."
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            disabled={isSaving}
            $overLimit={isOverWordLimit}
            aria-invalid={isOverWordLimit}
          />
          <WordCount
            data-testid="custom-instructions-word-count"
            data-over-limit={isOverWordLimit}
            $overLimit={isOverWordLimit}
          >
            {wordCount} / {MAX_FEEDBACK_INSTRUCTION_WORDS} words
          </WordCount>
          <ExamplesRow>
            {"Examples: "}
            {EXAMPLE_CUSTOM_INSTRUCTIONS.map((example, index) => (
              <React.Fragment key={example.name}>
                <ExampleLink
                  onClick={() => handleExampleClick(example)}
                  role="button"
                  tabIndex={0}
                >
                  {example.name}
                </ExampleLink>
                {index < EXAMPLE_CUSTOM_INSTRUCTIONS.length - 1 && ", "}
              </React.Fragment>
            ))}
          </ExamplesRow>
        </FieldGroup>

        <FooterRow>
          <CancelButton
            role="button"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClose();
              }
            }}
          >
            Cancel
          </CancelButton>
          <SaveButton
            className={canSave ? "" : "disabled"}
            role="button"
            tabIndex={canSave ? 0 : -1}
            aria-disabled={!canSave}
            onClick={() => {
              if (canSave) handleSave();
            }}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && canSave) {
                e.preventDefault();
                handleSave();
              }
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </SaveButton>
        </FooterRow>
      </ModalContainer>
    </Modal>
  );
}

export default CustomTypeModal;
